import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Loader2, ExternalLink, RefreshCw, Table2 } from 'lucide-react';
import { base44 } from '@/api/base44Client';
import { toast } from 'sonner';
import { useQuery, useQueryClient } from '@tanstack/react-query';

/**
 * The shared contacts Sheet, on the Shows screen.
 *
 * Contacts are written as they are captured, edited and deleted, by the API,
 * after the save has already been answered. So the Sheet can run a little
 * behind, and a rep who was offline in a hall can leave it further behind than
 * that. "Catch up" writes whatever is missing and is safe to press twice: a
 * contact already in the sheet is matched on its id and updated in place
 * rather than added again.
 */
export default function SheetPanel({ isAdmin }) {
  const [busy, setBusy] = useState(null);
  const queryClient = useQueryClient();

  const { data: sheet } = useQuery({
    queryKey: ['sheet-status'],
    queryFn: () => base44.sheets.status(),
  });

  if (!sheet?.configured) return null;

  const run = async (what, fn, describe) => {
    setBusy(what);
    try {
      const result = await fn();
      queryClient.invalidateQueries({ queryKey: ['sheet-status'] });
      describe(result);
    } catch (err) {
      toast.error(err.message || 'Google refused that');
    } finally {
      setBusy(null);
    }
  };

  const pending = sheet.not_yet_in_the_sheet ?? 0;

  return (
    <Card className="p-4 bg-card mb-5">
      <div className="flex items-center gap-2 mb-2">
        <Table2 className="w-4 h-4 text-accent" />
        <h3 className="font-semibold text-foreground text-sm flex-1">Contacts spreadsheet</h3>
        <a
          href={sheet.sheet_url}
          target="_blank"
          rel="noreferrer"
          className="text-xs text-accent hover:underline flex items-center gap-1"
        >
          Open <ExternalLink className="w-3 h-3" />
        </a>
      </div>

      <p className="text-xs text-muted-foreground">
        A tab per show, and a tab per rep for personal captures.{' '}
        {pending > 0
          ? <span className="text-amber-600">{pending} of {sheet.contacts} not written yet.</span>
          : <span>All {sheet.contacts} are in it.</span>}
      </p>

      {isAdmin && (
        <div className="flex gap-2 mt-3">
          <Button
            variant="outline"
            size="sm"
            disabled={busy !== null}
            onClick={() => run('backfill', () => base44.sheets.backfill(), (r) =>
              toast.success(r.written ? `${r.written} contacts written` : 'Already up to date'))}
            className="h-7 text-xs"
          >
            {busy === 'backfill'
              ? <Loader2 className="w-3 h-3 animate-spin" />
              : <><RefreshCw className="w-3 h-3 mr-1" />Catch up</>}
          </Button>

          <Button
            variant="ghost"
            size="sm"
            disabled={busy !== null}
            onClick={() => run('share', () => base44.sheets.share(), (r) => {
              // Sharing cannot work on a sheet TradeSync did not create. The
              // API says so in `note`; showing that beats a bare Google error.
              if (r.note) toast.warning(r.note);
              else toast.success(`Shared with ${r.shared_with.length} people`);
            })}
            className="h-7 text-xs text-muted-foreground"
          >
            {busy === 'share' ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Check sharing'}
          </Button>
        </div>
      )}
    </Card>
  );
}
